import {
  useState,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";

const ITEM_GAP = 8;

export const getNavTabsLeft = () => {
  const tabsEl = document.getElementById("header-tabs");
  if (tabsEl) return tabsEl.getBoundingClientRect().left;
  // Fallback: reserve the right ~38% of the screen for navbar tabs/logo.
  return window.innerWidth * 0.62;
};

/**
 * Measures the toolbar width up to the navbar tabs and decides which items fit
 * inline. The rest overflow into the "Pricer Tools" menu.
 *
 * @param {string[]} availableKeys item keys that can render, in toolbar order
 * @param {string[]} pinnedKeys keys that always stay inline (must be stable)
 * @param {string[]} priority keys by overflow priority; earlier = last to
 *   leave the bar (must be stable)
 */
const useToolbarOverflow = (availableKeys, pinnedKeys, priority) => {
  const [visibleKeys, setVisibleKeys] = useState(() => pinnedKeys);
  const [isToolbarReady, setIsToolbarReady] = useState(false);
  const [reserveRight, setReserveRight] = useState(() => {
    if (typeof window === "undefined") return 0;
    const navTabsLeft = getNavTabsLeft();
    return Math.max(window.innerWidth - navTabsLeft + 12, 0);
  });

  const measureRefs = useRef({});
  const menuMeasureRef = useRef(null);
  const containerRef = useRef(null);
  const resizeRafRef = useRef(0);

  // Measure available width up to the navbar tabs and decide how many items fit.
  const recomputeOverflow = useCallback(
    ({ requireMeasures = false } = {}) => {
      const navTabsLeft = getNavTabsLeft();
      setReserveRight(Math.max(window.innerWidth - navTabsLeft + 12, 0));

      const available = navTabsLeft - 15 - 12; // left inset + gap before tabs

      if (requireMeasures && availableKeys.length > 0) {
        const hasValidMeasures = availableKeys.every(
          (key) => (measureRefs.current[key]?.offsetWidth ?? 0) > 0
        );
        if (!hasValidMeasures) return false;
      }

      // Pinned items always consume space; the rest compete for what remains.
      const pinnedWidths = pinnedKeys.filter((k) =>
        availableKeys.includes(k)
      ).reduce(
        (sum, k) =>
          sum + (measureRefs.current[k]?.offsetWidth || 0) + ITEM_GAP,
        0
      );

      // Flex keys sorted by overflow priority (highest priority = last to leave).
      const flexKeys = priority.filter(
        (k) => availableKeys.includes(k) && !pinnedKeys.includes(k)
      );
      const widths = flexKeys.map((key) => ({
        key,
        width: measureRefs.current[key]?.offsetWidth || 0,
      }));

      const totalAll =
        pinnedWidths +
        widths.reduce((sum, item) => sum + item.width, 0) +
        ITEM_GAP * Math.max(widths.length - 1, 0);

      let fittedKeys;
      if (totalAll <= available) {
        fittedKeys = availableKeys;
      } else {
        const menuWidth = menuMeasureRef.current?.offsetWidth || 120;
        let used = menuWidth + ITEM_GAP + pinnedWidths;
        fittedKeys = [];
        for (let i = 0; i < widths.length; i += 1) {
          const next = widths[i].width + ITEM_GAP;
          if (used + next <= available) {
            used += next;
            fittedKeys.push(widths[i].key);
          }
          // Don't break — lower-priority items may still fit if a wide one didn't.
        }
        pinnedKeys.forEach((k) => {
          if (availableKeys.includes(k)) fittedKeys.push(k);
        });
      }

      // availableKeys is already in toolbar (render) order.
      const nextVisible = availableKeys.filter((k) => fittedKeys.includes(k));

      setVisibleKeys((prev) => {
        if (
          prev.length === nextVisible.length &&
          prev.every((key, idx) => key === nextVisible[idx])
        ) {
          return prev;
        }
        return nextVisible;
      });

      setIsToolbarReady(true);
      return true;
    },
    [availableKeys, pinnedKeys, priority]
  );

  const scheduleRecomputeOverflow = useCallback(
    (options) => {
      if (resizeRafRef.current) {
        cancelAnimationFrame(resizeRafRef.current);
      }
      resizeRafRef.current = requestAnimationFrame(() => {
        resizeRafRef.current = 0;
        recomputeOverflow(options);
      });
    },
    [recomputeOverflow]
  );

  useLayoutEffect(() => {
    let initialRafId = 0;
    const runInitial = () => {
      if (recomputeOverflow({ requireMeasures: true })) return;
      initialRafId = requestAnimationFrame(runInitial);
    };
    runInitial();

    const onResize = () => scheduleRecomputeOverflow();

    window.addEventListener("resize", onResize);

    const observedElements = [];
    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(onResize);
      [containerRef.current, document.getElementById("header-tabs")].forEach(
        (el) => {
          if (el) {
            resizeObserver.observe(el);
            observedElements.push(el);
          }
        }
      );
    }

    return () => {
      if (initialRafId) cancelAnimationFrame(initialRafId);
      if (resizeRafRef.current) cancelAnimationFrame(resizeRafRef.current);
      window.removeEventListener("resize", onResize);
      if (resizeObserver) {
        observedElements.forEach((el) => resizeObserver.unobserve(el));
        resizeObserver.disconnect();
      }
    };
  }, [recomputeOverflow, scheduleRecomputeOverflow, availableKeys]);

  return {
    visibleKeys,
    isToolbarReady,
    reserveRight,
    containerRef,
    measureRefs,
    menuMeasureRef,
    scheduleRecomputeOverflow,
  };
};

export default useToolbarOverflow;
